import { OptionType } from '@/common/presentation/components/forms/fields/FormikAutocomplete';
import { RaptorJobFormValues } from '@/raptorjob/presentation/RaptorJobValidationSchema';
import { useListCustomersAutocomplete } from '@/jobs/application/adapters/listCustomersAutocomplete';
import { useState, useMemo } from 'react';
import { useFormikContext } from 'formik';
import { ALLOWED_CALLER_ID_TYPES_SET } from '@/customers/application/types/CustomerTypes';
import { ContactOption, useContactOptions } from '@/common/presentation/hooks/useContactOptions';
import { useGetCustomerCallerId } from '@/customers/application/adapters/getCustomerCallerId';
import { findOptionByValue, hasOptionWithValue } from '@/common/presentation/utils/autocompleteUtils';

type CustomerOption = { id?: number | null; companyName?: string | null };

export const useCustomerInfoSection = () => {
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');

  const {
    options: customerOptions,
    loading: customerLoading,
    filterOptions: customerFilterOptions,
    rawCustomerData
  } = useListCustomersAutocomplete({ searchTerm: customerSearchTerm });

  const { values } = useFormikContext<RaptorJobFormValues>();

  const getSelectedCustomerData = (): CustomerOption | undefined => {
    if (!values.customerId || !rawCustomerData?.length) return undefined;
    return rawCustomerData.find((customer: CustomerOption) => customer.id === values.customerId);
  };

  const {
    loading: customerCallerLoading,
    contacts: customerContacts,
    customerDetails
  } = useGetCustomerCallerId(values.customerId ?? undefined, getSelectedCustomerData());

  const { options: contactOptions, initialCallerId } = useContactOptions({
    contacts: customerContacts,
    rawCaller: undefined,
    allowedTypes: ALLOWED_CALLER_ID_TYPES_SET
  });

  const customerInitialOption = useMemo((): OptionType | undefined => {
    if (!values.customerId) return undefined;

    const selectedCustomerInOptions = findOptionByValue(customerOptions, values.customerId);

    if (selectedCustomerInOptions) return selectedCustomerInOptions as OptionType;

    const selectedCustomerData =
      values.customerId && rawCustomerData?.length
        ? rawCustomerData.find((customer: CustomerOption) => customer.id === values.customerId)
        : undefined;

    const customerName = values.customerName ?? selectedCustomerData?.companyName ?? customerDetails?.companyName ?? 'Unknown Customer';

    return {
      value: values.customerId,
      label: customerName
    };
  }, [customerOptions, values.customerId, values.customerName, customerDetails?.companyName, rawCustomerData]);

  const customerOptionsWithInitial = useMemo(() => {
    if (!customerInitialOption) return customerOptions;

    if (!hasOptionWithValue(customerOptions, customerInitialOption.value)) {
      return [...customerOptions, customerInitialOption];
    }

    return customerOptions;
  }, [customerOptions, customerInitialOption]);

  const getContactAutocompleteOptions = (): OptionType[] => {
    return (
      contactOptions.reduce((acc: ContactOption[], opt: ContactOption) => {
        if (!acc.some((o) => o.name === opt.name)) {
          acc.push(opt);
        }
        return acc;
      }, []) ?? []
    ).map((opt) => ({ value: opt.id, label: opt.name }));
  };
  const getContactInitialOption = (): OptionType | undefined => {
    if (initialCallerId == null) return undefined;
    const found = getContactAutocompleteOptions().find((o) => String(o.value) === String(initialCallerId));
    return found;
  };

  return {
    customerSearchTerm,
    setCustomerSearchTerm,
    customerOptions: customerOptionsWithInitial,
    customerLoading,
    customerFilterOptions,
    customerCallerLoading,
    customerContacts,
    contactOptions,
    initialCallerId,
    getContactAutocompleteOptions,
    getContactInitialOption,
    customerInitialOption,
    values
  };
};
